export enum JourneyState {
  Draft = 'draft',
  Offered = 'offered',
  Assigned = 'assigned',
  Accepted = 'accepted',
  Started = 'started',
  Finished = 'finished',
  Transfering = 'transfering',
}

export enum ActionType {
  Collect = 'collect',
  Deliver = 'deliver',
  Wait = 'wait',
  Return = 'return',
}

export enum ActionStatus {
  Pending = 'PENDING',
  Canceled = 'CANCELED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Forced_end = 'FORCED_END',
}

export enum GeolocationType {
  DistributionCenter = 'DistributionCenter',
  Waypoint = 'Waypoint',
  DriverLocation = 'DriverLocation',
  CustomAddress = 'CustomAddress',
}

export enum ServiceType {
  Express = 'Express',
  Scheduled = 'Scheduled',
}

export type IssueBlame = 'schedule' | 'shipment' | 'group' | 'data';

export interface Issue {
  reason: string;
  reasonDescription: string;
  blame: IssueBlame;
  exceededBySeconds?: number;
}

export interface TimeWindow {
  from: string;
  to: string;
  date: string;
  utcOffset?: string;
}

export type JourneyKind = 'shipments' | 'collections' | 'mixed';

// Qué lleva una ruta de cada tipo. Espejo de models/JourneyKind.go en ms-journey,
// que es quien rechaza de verdad: acá solo decide qué paneles y botones mostrar.
const KIND_RULES: Record<
  JourneyKind,
  { shipments: boolean; collections: boolean }
> = {
  shipments: { shipments: true, collections: false },
  collections: { shipments: false, collections: true },
  mixed: { shipments: true, collections: true },
};
export const kindRules = (kind?: JourneyKind) =>
  KIND_RULES[kind ?? 'shipments'];
export const JOURNEY_KINDS = Object.keys(KIND_RULES) as JourneyKind[];

export interface Journey {
  id?: string;
  journeyId?: string;
  state?: JourneyState;
  kind?: JourneyKind;
  name?: string;

  creationDate?: Date;
  startedDate?: Date;
  offeredDate?: Date;
  acceptedDate?: Date;

  driver?: Driver;
  driverId?: number;
  driverName?: string;

  shipments?: Shipment[];
  originAddress?: Waypoint;
  destinationAddress?: Waypoint;

  estimatedDistance?: number;
  estimatedTime?: number;
  waypoints?: Waypoint[];
  actionsWithoutWaypoints?: Action[];
  geometry?: string;

  schedule?: TimeWindow;
  issue?: Issue;

  totalPackages?: number;
  deliveredPackages?: number;
  failedPackages?: number;
  pendingPackages?: number;

  collections?: JourneyCollection[];
  collectionWaitMinutes?: number;
  vehiclePlate?: string;

  totalCollections?: number;
  collectedCollections?: number;
  failedCollections?: number;
  pendingCollections?: number;
}

export interface Driver {
  id?: number;
  active?: boolean;
  name?: string;
  lastName?: string;
  fullname?: string;
  avatar?: string;
  email?: string;
  city?: {
    dialingCode?: string;
  };
  mobile?: string;
}

export interface Waypoint {
  id?: number;
  order?: number;
  address?: string;
  latitude?: number;
  longitude?: number;
  actions?: Action[];
  label?: GeolocationType;
  eta?: number;
  centerDeliveries?: { total: number; received: number };
}

export interface Action {
  routeShipmentId?: number;
  actionType?: ActionType;
  status?: ActionStatus;
  issue?: Issue;

  collectionId?: number;
  serviceSeconds?: number;
  arrivedAt?: string;
  leaveAllowedAt?: string;
  waitMeasuredOnDevice?: boolean;
}

export interface JourneyCollection {
  id: number;
  storeName: string;
  marketplaceName: string;
  geolocation: {
    address?: string;
    latitude?: number;
    longitude?: number;
    label?: string;
  };
  scheduledStart: string;
  estimatedDurationMinutes: number;
}

export interface Shipment {
  id: number;
  externalCode?: string;
  packageSize?: number;
  status?: number;
  statusDescription?: string;
  hasDriver?: boolean;
  driverId?: number;

  destinationAddress?: string;
  originAddress?: string;

  unavailable?: boolean;
  schedule?: TimeWindow;
  issue?: Issue;
}

export interface AuditLog {
  action?: string;
  journeyId?: string;
  performedBy?: string;
  performer?: 'operator' | 'driver' | 'system';
  message?: string;
  timestamp?: Date;
  extras?: Record<string, JSON>;
}
